import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * THE EXPAND / COLLAPSE ANIMATION — one rule for every disclosure that opens or
 * closes (owner 2026-10-09, demo run-through: "on mobile when we expand a
 * order/hire etc can we animate it please … a smooth expand would be nice",
 * "same with expanding each part of the order … and individual items within
 * line items", then — asked about desktop and closing — "yes go ahead with
 * both").
 *
 * - `useExpandAnimation(ref, phaseKey, { animateOnMount })` — whenever
 *   `phaseKey` changes, the element's HEIGHT eases from what it measured last
 *   time to what it measures now.
 * - `useAnimatedPresence(open, ref)` → `present` — keeps a body MOUNTED (or
 *   un-hidden) for the length of a closing ease to 0, then lets it go. Render
 *   with `present`, not `open`, or the close cannot run. Same reason
 *   `TableRowExpansion.js`'s `ExpandedPanelRow` takes `open`: render it ALWAYS.
 * - `AnimatedHeight` — a `<div>` doing both (`open` defaults to true).
 * - `animateHeight(el, from, to)` — the one primitive, for a caller tracking
 *   heights itself (OrderForm's line-item cards, rendered in a map).
 *
 * Timing: 240ms on a phone, 200ms on a desktop (a long ease on a tall desktop
 * panel feels sluggish), 180ms to close. Never for prefers-reduced-motion.
 *
 * 🔑 Web Animations on the MEASURED height, not a CSS transition: a transition
 * cannot run from `height: auto`, and a `max-height` trick eases at the wrong
 * speed for every size but one. `overflow: hidden` only while it runs, so
 * nothing (a menu, a focus ring) is clipped afterwards.
 * 🪤 The FIRST render only records the height unless `animateOnMount` — a
 * section inside a freshly opened order must not animate on top of the order's
 * own opening.
 * 🪤 A close keeps the CONTENT mounted (same element tree, so nothing remounts
 * and no request fires) and ends `fill: 'forwards'` at 0, so the body never
 * flashes back to full height for the frame before it unmounts. Re-opening
 * mid-close cancels the close.
 * 🔻 The Breakdown's phone gag panel keeps its OWN older close (a grid-rows
 * transition in `components/gags/GagGroupTable.js`); fold it in when next touched.
 * jsdom has no `Element.animate`, so there everything opens and closes at once
 * exactly as before — the motion itself is a BROWSER check.
 */
const PHONE_OPEN_MS = 240;
const DESKTOP_OPEN_MS = 200;
const CLOSE_MS = 180;
export const EXPAND_ANIMATION_MS = PHONE_OPEN_MS;

function motionAllowed() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function webAnimationsAvailable() {
  return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
}
function canAnimate(el) {
  return !!el && typeof el.animate === 'function' && motionAllowed();
}
function openDuration() {
  return window.matchMedia('(max-width: 767px)').matches ? PHONE_OPEN_MS : DESKTOP_OPEN_MS;
}

/**
 * Ease `el` from `from`px to `to`px tall. Returns the Animation, or null when
 * nothing ran (no motion, nothing to do, no Web Animations).
 */
export function animateHeight(el, from, to) {
  if (from === to || !canAnimate(el)) return null;
  const closing = to === 0;
  const prevOverflow = el.style.overflow;
  el.style.overflow = 'hidden';
  const anim = el.animate(
    [{ height: `${from}px`, opacity: from === 0 ? 0 : 1 }, { height: `${to}px`, opacity: closing ? 0 : 1 }],
    { duration: closing ? CLOSE_MS : openDuration(), easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: closing ? 'forwards' : 'none' }
  );
  const done = () => { el.style.overflow = prevOverflow; };
  anim.addEventListener('finish', done);
  anim.addEventListener('cancel', done);
  return anim;
}

export function useExpandAnimation(ref, phaseKey, { animateOnMount = false } = {}) {
  const lastHeight = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    // Unmounted / hidden: forget its height, so the NEXT open eases from 0
    // again rather than from its old full height.
    if (!el || el.offsetHeight === 0) { lastHeight.current = el ? 0 : null; return; }
    const to = el.offsetHeight;
    const first = lastHeight.current === null;
    const from = first ? 0 : lastHeight.current;
    lastHeight.current = to;
    if (first && !animateOnMount) return;
    animateHeight(el, from, to);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseKey]);
}

export function useAnimatedPresence(open, ref) {
  // Derived IN RENDER, never a frame late: the render in which `open` turns
  // false must still render the body, or it would unmount and remount.
  const [state, setState] = useState({ open, closing: false });
  let closing = state.closing;
  if (state.open !== open) {
    closing = !open && motionAllowed() && webAnimationsAvailable();
    setState({ open, closing });
  }
  useLayoutEffect(() => {
    if (!closing) return undefined;
    const el = ref.current;
    const anim = el ? animateHeight(el, el.offsetHeight, 0) : null;
    const finish = () => setState((s) => (s.open ? s : { ...s, closing: false }));
    if (!anim) { finish(); return undefined; }
    anim.addEventListener('finish', finish);
    return () => { anim.removeEventListener('finish', finish); anim.cancel(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);
  return open || closing;
}

/**
 * A `<div>` that eases open (`phaseKey` changes, or on mount when
 * `animateOnMount`) and, given `open`, eases closed before unmounting. Every
 * other prop passes through untouched.
 */
export function AnimatedHeight({ open = true, phaseKey, animateOnMount = false, children, ...divProps }) {
  const ref = useRef(null);
  const present = useAnimatedPresence(open, ref);
  useExpandAnimation(ref, present ? phaseKey : '__absent__', { animateOnMount });
  if (!present) return null;
  return <div ref={ref} {...divProps}>{children}</div>;
}

export default useExpandAnimation;
